/**
 * Vendoré depuis front-end/src/lib/api.ts (2026-08-04).
 * Divergences volontaires : aucune.
 * Toute correction ici doit être reportée dans front-end/ (et inversement).
 * Vérifier avec `pnpm check:drift`.
 */
import ky, { type AfterResponseHook, type BeforeRequestHook, HTTPError, type KyResponse } from 'ky'

const API_BASE =
  (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:4000/api/v1'

const isBrowser = typeof window !== 'undefined'

// ── Access token in memory (not localStorage) ──────────────────────────────
// The refresh token lives in an HttpOnly cookie set by the server.
// XSS cannot steal the refresh token; the access token is short-lived (15 min).
let _accessToken: string | null = null

export function getAccessToken(): string | null {
  return _accessToken
}

export function setAccessToken(token: string) {
  _accessToken = token
}

export function setTokens(accessToken: string) {
  _accessToken = accessToken
}

export function clearTokens() {
  _accessToken = null
}

let isRefreshing = false
let refreshQueue: Array<(token: string) => void> = []
let _silentRefreshPromise: Promise<boolean> | null = null

const authChannel = isBrowser ? new BroadcastChannel('auth') : null

if (authChannel) {
  authChannel.onmessage = ({ data }: MessageEvent<{ type: string }>) => {
    if (data?.type === 'signOut' && !window.location.pathname.startsWith('/connexion')) {
      clearTokens()
      window.location.href = '/connexion'
    }
  }
}

export function signOut() {
  const token = _accessToken
  clearTokens()
  authChannel?.postMessage({ type: 'signOut' })
  // Best-effort server logout to clear the HttpOnly cookie
  if (isBrowser && token) {
    void ky
      .post(`${API_BASE}/auth/logout`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` },
        retry: 0,
      })
      .catch(() => {})
  }
  if (isBrowser) window.location.href = '/connexion'
}

async function doRefresh(): Promise<string | null> {
  if (!isBrowser) return null

  try {
    // Route dédiée à la PWA : elle lit `ekklesia_refresh_mobile`, distinct du
    // cookie du front staff. Les deux applications parlent à la même API, sur le
    // même hôte ; sous un seul nom de cookie, la dernière connexion écrasait
    // l'autre et le jeton tournant détruisait la session restée en arrière.
    const res = await ky
      .post(`${API_BASE}/auth/mobile/refresh`, { credentials: 'include', retry: 0 })
      .json<{ data: { accessToken: string } }>()
    _accessToken = res.data.accessToken
    return _accessToken
  } catch {
    _accessToken = null
    return null
  }
}

// Exported for use in app startup (silent re-authentication after page reload)
export function silentRefresh(): Promise<boolean> {
  if (_silentRefreshPromise) return _silentRefreshPromise

  _silentRefreshPromise = doRefresh().then((token) => {
    _silentRefreshPromise = null
    return token !== null
  })

  return _silentRefreshPromise
}

const injectAuth: BeforeRequestHook = ({ request }) => {
  const token = getAccessToken()
  if (token) {
    request.headers.set('Authorization', `Bearer ${token}`)
  }
}

const handleUnauthorized: AfterResponseHook = async ({ request, response }) => {
  if (response.status !== 401 || !getAccessToken()) return response

  if (isRefreshing) {
    const newToken = await new Promise<string>((resolve) => {
      refreshQueue.push(resolve)
    })
    request.headers.set('Authorization', `Bearer ${newToken}`)
    return ky(request)
  }

  isRefreshing = true
  const newToken = await doRefresh()
  isRefreshing = false

  if (!newToken) {
    refreshQueue = []
    signOut()
    return response
  }

  for (const resolve of refreshQueue) resolve(newToken)
  refreshQueue = []

  request.headers.set('Authorization', `Bearer ${newToken}`)
  return ky(request)
}

export const api = ky.create({
  baseUrl: `${API_BASE}/`,
  credentials: 'include',
  timeout: 30000,
  retry: 0,
  hooks: {
    beforeRequest: [injectAuth],
    afterResponse: [handleUnauthorized],
  },
})

export type ApiResponse<T> = { success: true; data: T; meta?: Record<string, unknown> }

export interface PaginatedMeta {
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface Paginated<T> {
  data: T[]
  meta: PaginatedMeta
}

type SearchParams = Record<string, string | number | boolean | undefined>

function buildSearchParams(params?: SearchParams): Record<string, string> | undefined {
  if (!params) return undefined
  return Object.fromEntries(
    Object.entries(params)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => [k, String(v)]),
  )
}

/**
 * Déballe l'enveloppe `{ success, data }` en tolérant une réponse sans corps.
 *
 * Plusieurs endpoints répondent 204 — `auth/mobile/request-otp`,
 * `members/:id/transfer` — et `.json()` sur un corps vide lève une
 * `SyntaxError`. N'étant pas une `HTTPError`, elle se traduit à tort en erreur
 * réseau alors que la requête a parfaitement abouti.
 */
async function unwrap<T>(response: KyResponse): Promise<T> {
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T
  }
  const res = await response.json<ApiResponse<T>>()
  return res.data
}

export async function apiGet<T>(path: string, searchParams?: SearchParams): Promise<T> {
  const res = await api
    .get(path, { searchParams: buildSearchParams(searchParams) })
    .json<ApiResponse<T>>()
  return res.data
}

export async function apiGetPaginated<T>(
  path: string,
  searchParams?: SearchParams,
): Promise<Paginated<T>> {
  const res = await api
    .get(path, { searchParams: buildSearchParams(searchParams) })
    .json<{ success: true; data: T[]; meta: PaginatedMeta }>()
  return { data: res.data, meta: res.meta }
}

export async function apiPost<T>(path: string, json: unknown): Promise<T> {
  return unwrap<T>(await api.post(path, { json }))
}

export async function apiPatch<T>(path: string, json: unknown): Promise<T> {
  return unwrap<T>(await api.patch(path, { json }))
}

export async function apiPut<T = void>(path: string, json: unknown): Promise<T> {
  return unwrap<T>(await api.put(path, { json }))
}

export async function apiDelete<T = void>(path: string): Promise<T> {
  return unwrap<T>(await api.delete(path))
}

export async function apiUpload<T>(path: string, file: File): Promise<T> {
  const body = new FormData()
  body.append('file', file)
  return unwrap<T>(await api.post(path, { body }))
}

export { HTTPError }
